import { type ReactNode, useEffect, useState } from "react";

import { useApiUrl } from "@refinedev/core";
import { axiosInstance } from "@refinedev/simple-rest";

import {
  ListView,
  ListViewHeader,
} from "@/components/refine-ui/views/list-view";

import { useArtistRepository } from "../artists/hooks/use-artist-repository";
import { useEventRepository } from "../events/hooks/use-event-repository";
import { useGenreRepository } from "../genres/hooks/use-genre-repository";
import { useVenueRepository } from "../venues/hooks/use-venue-repository";
import { RecommendationResults } from "./components/RecommendationResults";
import { DecayProfileEditor } from "./components/decayEditor";
import { EmbeddingInspector } from "./components/embeddingInspector";
import { InteractionBuilder } from "./components/interactionBuilder";
import { Toolbar } from "./components/toolbar";
import { InteractionWeightEditor } from "./components/weightEditor";
import { useSandboxDecay } from "./hooks";
import { usePersonaInsights } from "./hooks/usePersonaInsights";
import useInteractionBuilder from "./hooks/useInteractionBuilder";
import type { EntityRecommendations } from "./hooks/useSandboxPersonas";
import { useSandboxPersonas } from "./hooks/useSandboxPersonas";
import useSandboxWeights from "./hooks/useSandboxWeights";
import { useUserInteractions } from "./hooks/useUserInteractions";
import type { SandboxEntity } from "./types/entities";
import type { Interaction } from "./types/interactions";
import { VectorTerm } from "./types/vectorTerm";
import { calculateUserEmbedding } from "./utils/embeddingUtils";
import { buildServerEvidenceMap, computeEvidence } from "./utils/evidenceUtils";

const RecommendationSandbox = () => {
  const { nearestNeighbours: nearestArtists } = useArtistRepository();
  const { nearestNeighbours: nearestGenres } = useGenreRepository();
  const { nearestNeighbours: nearestEvents } = useEventRepository();
  const { nearestNeighbours: nearestVenues } = useVenueRepository();

  const apiUrl = useApiUrl();

  const {
    state: builderState,
    setEntityType,
    setSelectedEntity,
    setInteractionType,
    setSelectedPeriod,
    addInteraction,
    removeInteraction,
    resetInteractions,
    updateInteractionEmbedding,
  } = useInteractionBuilder();

  const { fetchUser, isLoading: isLoadingUser } = useUserInteractions(apiUrl);

  const {
    personas,
    currentPersona,
    newPersona,
    loadPersona,
    savePersona,
    deletePersona,
    exportPersona,
    loadPersonaFromImport,
  } = useSandboxPersonas();

  const { weights, getWeight, updateWeight, resetToDefaults, importWeights } =
    useSandboxWeights();

  const {
    profiles,
    assignments,
    getDefaultProfileId,
    setAssignment,
    addProfile,
    updateProfile,
    deleteProfile,
    resetToDefaults: resetDecayToDefaults,
    getDecayMultiplier,
    importConfig: importDecayConfig,
  } = useSandboxDecay();

  const [personaName, setPersonaName] = useState("New persona");
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);
  const [loadedUserEmbedding, setLoadedUserEmbedding] = useState<
    number[] | null
  >(null);
  const [userEmbedding, setUserEmbedding] = useState<number[] | null>(null);
  const [computedInteractions, setComputedInteractions] = useState<
    Interaction[] | null
  >(null);
  const [entityRecommendations, setEntityRecommendations] =
    useState<EntityRecommendations | null>(null);
  const [isComputing, setIsComputing] = useState(false);

  const {
    description,
    comparison,
    isLoadingDescription,
    isLoadingComparison,
    describePersona,
    compareWithResults,
    reset: resetInsights,
  } = usePersonaInsights(apiUrl);

  function resetComputedState() {
    setUserEmbedding(null);
    setComputedInteractions(null);
    setEntityRecommendations(null);
    resetInsights();
  }

  function clearUserLoad() {
    setLoadedUserId(null);
    setLoadedUserEmbedding(null);
  }

  // Sync local state when a persona is loaded
  useEffect(() => {
    if (currentPersona) {
      setPersonaName(currentPersona.name);
      resetComputedState();
    }
  }, [currentPersona?.id]);

  function handleNew() {
    const p = newPersona();
    setPersonaName(p.name);
    clearUserLoad();
    resetInteractions([]);
    resetComputedState();
  }

  function handleLoad(id: string) {
    clearUserLoad();
    loadPersona(id);
  }

  function handleSave() {
    savePersona(personaName, builderState.interactions);
  }

  function handleDelete() {
    if (!currentPersona) return;
    deletePersona(currentPersona.id);
    setPersonaName("New persona");
    clearUserLoad();
    resetInteractions([]);
    resetComputedState();
  }

  async function handleLoadUser(userId: string) {
    const { embedding, stubs } = await fetchUser(userId);

    // Build stub interactions immediately with loading state
    const stubInteractions: Interaction[] = stubs.map(
      ({ interactionId, entityType, entityId, interactionType }) => ({
        id: interactionId,
        entityType,
        interactionType,
        entity: {
          id: entityId,
          name: entityId,
          isLoadingEmbedding: true,
        } as SandboxEntity,
        decayPeriod: "q1",
      }),
    );

    resetComputedState();
    setPersonaName(`User ${userId}`);
    setLoadedUserId(userId);
    setLoadedUserEmbedding(embedding);
    resetInteractions(stubInteractions);

    // Immediately fetch recommendations using the server-computed embedding
    if (embedding) {
      runCompute(stubInteractions, embedding, userId);
    }

    // Fetch entity data + embedding per stub in the background
    for (const { interactionId, entityType, entityId } of stubs) {
      Promise.all([
        axiosInstance.get(`${apiUrl}/${entityType}s/${entityId}`, {
          params: { "api-version": "1.0" },
        }),
        axiosInstance.get(
          `${apiUrl}/${entityType}s/${entityId}/embeddings/latest`,
          { params: { "api-version": "1.0" } },
        ),
      ])
        .then(([entityResp, embResp]) => {
          const data = entityResp.data;
          const name =
            data.name ?? data.document?.name ?? entityId;
          updateInteractionEmbedding(
            interactionId,
            embResp.data?.embedding ?? null,
            undefined,
            undefined,
            name,
          );
        })
        .catch(() => {
          updateInteractionEmbedding(interactionId, null, "Failed to fetch");
        });
    }
  }

  function handleDescribePersona() {
    if (!computedInteractions) return;
    describePersona(computedInteractions);
  }

  function handleCompareWithResults() {
    if (!entityRecommendations) return;
    compareWithResults(entityRecommendations);
  }

  function handleExportPersona() {
    if (!currentPersona) return;
    exportPersona({
      ...currentPersona,
      name: personaName,
      interactions: builderState.interactions,
    });
  }

  function handleExportConfig() {
    const payload = { type: "sandbox-config", weights, profiles, assignments };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sandbox-config-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleImportConfig(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string);
        if (parsed.type !== "sandbox-config") return;
        if (parsed.weights) importWeights(parsed.weights);
        if (parsed.profiles && parsed.assignments)
          importDecayConfig(parsed.profiles, parsed.assignments);
      } catch {
        // Silently ignore malformed imports
      }
    };
    reader.readAsText(file);
  }

  function handleImportPersona(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string);
        const imported = { ...parsed, id: crypto.randomUUID() };
        loadPersonaFromImport(imported);
        resetInteractions(imported.interactions ?? []);
        for (const interaction of imported.interactions ?? []) {
          const entityId = interaction.entity?.id;
          if (!entityId) continue;
          axiosInstance
            .get(
              `${apiUrl}/${interaction.entityType}s/${entityId}/embeddings/latest`,
              { params: { "api-version": "1.0" } },
            )
            .then((response) =>
              updateInteractionEmbedding(interaction.id, response.data.embedding),
            )
            .catch(() =>
              updateInteractionEmbedding(
                interaction.id,
                null,
                "Failed to fetch embedding",
              ),
            );
        }
      } catch {
        // Silently ignore malformed imports
      }
    };
    reader.readAsText(file);
  }

  const mapInteractionToVectorTerm = (interaction: Interaction): VectorTerm => {
    const decayMultiplier = getDecayMultiplier(interaction);
    const interactionWeight = getWeight(
      interaction.entityType,
      interaction.interactionType,
    );
    return {
      interaction,
      decayWeight: decayMultiplier,
      interactionWeight,
    };
  };

  const runCompute = async (
    interactions: Interaction[],
    overrideEmbedding?: number[] | null,
    overrideUserId?: string | null,
  ) => {
    setIsComputing(true);

    try {
      // Fetch embeddings for interactions that don't already have one
      const missing = interactions.filter((i) => !i.entity.embedding);
      let resolvedInteractions = interactions;

      if (missing.length > 0) {
        const results = await Promise.allSettled(
          missing.map(async (i) => {
            const resp = await axiosInstance.get(
              `${apiUrl}/${i.entityType}s/${i.entity.id}/embeddings/latest`,
              { params: { "api-version": "1.0" } },
            );
            return { id: i.id, embedding: resp.data.embedding as number[] };
          }),
        );

        const embeddingMap = new Map<string, number[] | null>();
        results.forEach((result, idx) => {
          const id = missing[idx].id;
          if (result.status === "fulfilled") {
            embeddingMap.set(id, result.value.embedding);
            updateInteractionEmbedding(id, result.value.embedding);
          } else {
            embeddingMap.set(id, null);
            updateInteractionEmbedding(id, null, "Failed to fetch embedding");
          }
        });

        resolvedInteractions = interactions.map((i) =>
          embeddingMap.has(i.id)
            ? {
                ...i,
                entity: {
                  ...i.entity,
                  embedding: embeddingMap.get(i.id) ?? undefined,
                },
              }
            : i,
        );
      }

      const vectorTerms: VectorTerm[] = resolvedInteractions
        .filter((i) => i.entity.embedding != null)
        .map(mapInteractionToVectorTerm);

      const embedding =
        overrideEmbedding ??
        loadedUserEmbedding ??
        calculateUserEmbedding(vectorTerms);
      const userId =
        overrideUserId !== undefined ? overrideUserId : loadedUserId;

      setUserEmbedding(embedding);
      setComputedInteractions(resolvedInteractions);

      const [artistsResult, genresResult, venuesResult, eventsResult] =
        await Promise.all([
          nearestArtists(embedding, 20),
          nearestGenres(embedding, 20),
          nearestVenues(embedding, 20),
          nearestEvents(embedding, 20),
        ]);

      // Fetch server-side evidence for all candidates when a real user is loaded
      const evidenceMap = new Map<string, string>();
      if (userId) {
        const candidates = [
          ...(artistsResult ?? []).map((a) => ({
            EntityId: a.id,
            EntityType: "artist",
          })),
          ...(genresResult ?? []).map((g) => ({
            EntityId: g.id,
            EntityType: "genre",
          })),
          ...(venuesResult ?? []).map((v) => ({
            EntityId: v.id,
            EntityType: "venue",
          })),
          ...(eventsResult ?? []).map((e) => ({
            EntityId: e.id,
            EntityType: "event",
          })),
        ];
        try {
          const resp = await axiosInstance.post(
            `${apiUrl}/users/recommendations/evidence/query`,
            { UserId: userId, Candidates: candidates, EvidenceTopN: 5 },
            { params: { "api-version": "1.0" } },
          );
          buildServerEvidenceMap(resp.data ?? []).forEach((v, k) =>
            evidenceMap.set(k, v),
          );
        } catch {
          // fall through to client-side evidence
        }
      }

      const getEvidence = (
        id: string,
        genres: string[] | undefined,
        entityType: Parameters<typeof computeEvidence>[1],
      ) =>
        evidenceMap.get(id) ??
        computeEvidence(genres, entityType, vectorTerms);

      setEntityRecommendations({
        artists: (artistsResult ?? []).map((artist, index, arr) => {
          const genres = artist.genres
            ?.map((g) => g.name)
            .filter(Boolean) as string[] | undefined;
          return {
            id: artist.id,
            name: artist.name,
            url: `/artists/edit/${artist.id}`,
            similarity: (arr.length - index) / arr.length,
            subtitle: genres?.slice(0, 3).join(" · ") || undefined,
            photo: artist.media?.photos?.[0]?.url,
            genres,
            evidence: getEvidence(artist.id, genres, "artist"),
          };
        }),
        genres: (genresResult ?? []).map((genre, index, arr) => ({
          id: genre.id,
          name: genre.name,
          url: `/genres/edit/${genre.id}`,
          similarity: (arr.length - index) / arr.length,
          evidence: getEvidence(genre.id, undefined, "genre"),
        })),
        venues: (venuesResult ?? []).map((venue, index, arr) => ({
          id: venue.id,
          name: venue.name,
          url: `/venues/edit/${venue.id}`,
          similarity: (arr.length - index) / arr.length,
          subtitle:
            (venue.geodata?.["name"] as string | undefined) ||
            venue.area ||
            undefined,
          photo: venue.media?.photos?.[0]?.url,
          type: venue.type || undefined,
          address: venue.address || undefined,
          evidence: getEvidence(venue.id, undefined, "venue"),
        })),
        events: (eventsResult ?? []).map((event, index, arr) => {
          const parts: string[] = [];
          if (event.startDate) {
            parts.push(
              new Date(event.startDate * 1000).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              }),
            );
          }
          if (event.venue?.name) parts.push(event.venue.name);
          const eventGenres = event.genres
            ?.map((g) => g.name)
            .filter(Boolean) as string[] | undefined;
          return {
            id: event.id,
            name: event.name,
            url: `/events/edit/${event.id}`,
            similarity: (arr.length - index) / arr.length,
            subtitle: parts.length > 0 ? parts.join(" · ") : undefined,
            photo: event.media?.photos?.[0]?.url,
            type: event.type || undefined,
            venueName: event.venue?.name || undefined,
            genres: eventGenres,
            lineup: event.lineup
              ?.map((a) => a.name)
              .filter(Boolean) as string[] | undefined,
            evidence: getEvidence(event.id, eventGenres, "event"),
          };
        }),
      });
    } finally {
      setIsComputing(false);
    }
  };

  const handleCompute = (interactions: Interaction[]) => {
    if (interactions.length === 0) return;
    runCompute(interactions);
  };

  return (
    <ListView className="p-6 gap-6">
      <div className="flex items-center justify-between">
        <ListViewHeader title="Recommendation Sandbox" />
        <div className="flex items-center gap-2">
          <DecayProfileEditor
            profiles={profiles}
            assignments={assignments}
            onUpdateProfile={updateProfile}
            onAddProfile={addProfile}
            onDeleteProfile={deleteProfile}
            onSetAssignment={setAssignment}
            onReset={resetDecayToDefaults}
          />
          <InteractionWeightEditor
            weights={weights}
            onUpdate={updateWeight}
            onReset={resetToDefaults}
          />
        </div>
      </div>
      <Toolbar
        personas={personas}
        currentPersona={currentPersona}
        onNew={handleNew}
        onLoad={handleLoad}
        onSave={handleSave}
        onDelete={handleDelete}
        onExportPersona={handleExportPersona}
        onExportConfig={handleExportConfig}
        onImportPersona={handleImportPersona}
        onImportConfig={handleImportConfig}
        onLoadUser={handleLoadUser}
        isLoadingUser={isLoadingUser}
      />

      {/* Three-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        {/* Column 1: Interaction builder */}
        <div className="space-y-2">
          <SectionHeading>Interaction builder</SectionHeading>
          <InteractionBuilder
            personaName={personaName}
            onPersonaNameChange={setPersonaName}
            onCompute={handleCompute}
            isComputing={isComputing}
            profiles={profiles}
            getDefaultProfileId={getDefaultProfileId}
            state={builderState}
            onSetEntityType={setEntityType}
            onSetSelectedEntity={setSelectedEntity}
            onSetInteractionType={setInteractionType}
            onSetSelectedPeriod={setSelectedPeriod}
            onAddInteraction={addInteraction}
            onRemoveInteraction={removeInteraction}
          />
        </div>

        {/* Column 2: Embedding inspector */}
        <div className="space-y-2">
          <SectionHeading>Breakdown</SectionHeading>
          <EmbeddingInspector
            interactions={computedInteractions}
            userEmbedding={userEmbedding}
            personas={personas}
            currentPersonaId={currentPersona?.id}
            getWeight={getWeight}
            getDecayMultiplier={getDecayMultiplier}
            description={description}
            isLoadingDescription={isLoadingDescription}
            onDescribe={handleDescribePersona}
          />
        </div>

        {/* Column 3: Recommendations */}
        <div className="space-y-2">
          <SectionHeading>Recommendations</SectionHeading>
          <RecommendationResults
            recommendations={entityRecommendations}
            comparison={comparison}
            isLoadingComparison={isLoadingComparison}
            onCompare={handleCompareWithResults}
            canCompare={!!description}
          />
        </div>
      </div>
    </ListView>
  );
};

function SectionHeading({ children }: { children: ReactNode }) {
  return <h3 className="text-base font-semibold">{children}</h3>;
}

export default RecommendationSandbox;
